import { createContext, useContext, useReducer, type ReactNode } from "react";
import { setTripDateFromIso } from "./trip-date";

export interface CartItem {
  id: string;                // local cart-item id
  serviceId: string;
  serviceName: string;
  serviceType: string;
  unitId: string | null;
  unitName: string | null;
  description: string;
  quantity: number;
  unitPrice: number | null;  // null = price TBC (e.g. free restaurant reservation)
  currency: string;
  brandId: string | null;
  brandName: string | null;
  metadata: Record<string, unknown>;
}

/**
 * What a cart line is really worth, in cents.
 *
 * Lives beside CartItem rather than in the drawer because it is the cart's
 * money rule, not the drawer's: the panel's "In your visit" summary reads it
 * too, and that row showing a different total to the drawer is precisely the
 * bug this function exists to prevent.
 *
 * For a priced accommodation room the SERVER has already resolved the stay
 * total and stored it in metadata.amount_cents — from the Cloudbeds rate
 * calendar, a pricing rule, or the flat rate, whichever applies.
 * `item.unitPrice` is the WIDGET's display price and falls back to the unit's
 * local `base_price` when the seasonal resolve has not landed, so the two
 * disagree routinely: a room whose Cloudbeds rate is R3,150 carries a
 * base_price of R3,600.
 *
 * Deriving money from unitPrice × quantity therefore manufactured a balance
 * nobody owed — the guest was shown "pay R3,150 now, R450 at the venue" on a
 * booking that costs R3,150 in total. It was wrong a second way too:
 * `quantity` on an accommodation line is ROOMS, not nights, so the product is
 * one night's money on any multi-night stay.
 *
 * Trust the server's figure when there is one. Everything else keeps the old
 * arithmetic: an unpriced room (unitPrice null) is a reservation, and for
 * those amount_cents may hold a deposit rather than a total.
 */
export function lineGrossCents(item: CartItem): number | null {
  const md = item.metadata ?? {};
  // A line carrying its OWN deposit (deposit_cents) has a real two-part
  // arrangement — pay some now, settle the rest at the venue — and the gross
  // must stay the gross for that split to mean anything. Only lines whose
  // amount_cents IS the whole price are re-based here.
  const hasSeparateDeposit =
    typeof md.deposit_cents === "number" && md.deposit_cents > 0;
  if (
    item.serviceType === "accommodation" &&
    item.unitPrice != null &&
    !hasSeparateDeposit &&
    typeof md.amount_cents === "number" &&
    md.amount_cents > 0
  ) {
    return md.amount_cents;
  }
  return item.unitPrice != null
    ? Math.round(item.unitPrice * item.quantity * 100)
    : null;
}

interface CartState {
  items: CartItem[];
}

type CartAction =
  | { type: "ADD"; item: Omit<CartItem, "id"> }
  | { type: "REMOVE"; itemId: string }
  | { type: "UPDATE_QTY"; itemId: string; quantity: number }
  | { type: "UPDATE_PRICE"; itemId: string; unitPrice: number }
  | { type: "PATCH_METADATA"; itemId: string; patch: Record<string, unknown> }
  | { type: "CLEAR" };

function reducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "ADD": {
      const id = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
      return { items: [...state.items, { ...action.item, id }] };
    }
    case "REMOVE":
      return { items: state.items.filter((i) => i.id !== action.itemId) };
    case "UPDATE_QTY":
      return {
        items: state.items.map((i) =>
          i.id === action.itemId ? { ...i, quantity: action.quantity } : i,
        ),
      };
    case "PATCH_METADATA":
      return {
        items: state.items.map((i) =>
          i.id === action.itemId
            ? { ...i, metadata: { ...i.metadata, ...action.patch } }
            : i,
        ),
      };
    case "UPDATE_PRICE":
      return { items: state.items.map((item) => item.id === action.itemId
        ? { ...item, unitPrice: action.unitPrice } : item) };
    case "CLEAR":
      return { items: [] };
  }
}

interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  currency: string;
  add: (item: Omit<CartItem, "id">) => void;
  remove: (itemId: string) => void;
  updateQty: (itemId: string, quantity: number) => void;
  updatePrice: (itemId: string, unitPrice: number) => void;
  /**
   * Shallow-merges a patch into a single cart item's metadata. Used by
   * the cart's per-item editable controls (datetime + party stepper)
   * — they call this after the server-side update_booking_hold RPC
   * succeeds so the chip rail re-renders with the new values.
   */
  patchMetadata: (itemId: string, patch: Record<string, unknown>) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({
  children,
  currency = "ZAR",
}: {
  children: ReactNode;
  currency?: string;
}) {
  const [state, dispatch] = useReducer(reducer, { items: [] });

  const subtotal = state.items.reduce(
    (sum, i) => sum + (i.unitPrice ?? 0) * i.quantity,
    0,
  );

  const value: CartContextValue = {
    items: state.items,
    itemCount: state.items.reduce((n, i) => n + i.quantity, 0),
    subtotal,
    currency,
    add: (item) => {
      // An added booking pins the shared trip date — every add path (native,
      // ChiDesk, combos, events) funnels here, so the other tabs open on the
      // date the guest is actually planning around.
      setTripDateFromIso(item.metadata?.starts_at);
      dispatch({ type: "ADD", item });
    },
    remove: (itemId) => dispatch({ type: "REMOVE", itemId }),
    updateQty: (itemId, quantity) =>
      dispatch({ type: "UPDATE_QTY", itemId, quantity }),
    updatePrice: (itemId, unitPrice) => dispatch({ type: "UPDATE_PRICE", itemId, unitPrice }),
    patchMetadata: (itemId, patch) =>
      dispatch({ type: "PATCH_METADATA", itemId, patch }),
    clear: () => dispatch({ type: "CLEAR" }),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
